import type { KAPLAYCtx } from '../types.ts'
import type { RoomOptions } from '../types.ts'
import { useLang } from '../providers/i18n/i18n.ts'
import { useGameUiTextFrame } from '../objects/game-ui-text-frame.ts'
import { getTiledSize, parsePolygon, getFileCacheStr } from '../utils.ts'

export function useMothersRoom(k: KAPLAYCtx) {
  const cacheStr = getFileCacheStr()
  k.loadSprite('mothersRoom', `sprites/rooms/mothers-room.png?${cacheStr}`)
  k.loadSprite('mothersRoomHover', `sprites/rooms/mothers-room--hover.png?${cacheStr}`)
  const collidersJson = k.loadJSON(
    'mothersRoomColliders',
    `colliders/mothers-room.parsed.json?${cacheStr}`,
  )

  const { showTextFrame } = useGameUiTextFrame(k)
  const { getLangString } = useLang(k)

  function setMothersRoom(roomOpts?: RoomOptions) {
    roomOpts = roomOpts || {}

    const roomObj = k.add()

    k.onLoad(() => {
      const { data } = collidersJson

      roomObj.add([
        k.sprite('mothersRoom', {
          width: getTiledSize(data.width),
          height: getTiledSize(data.height),
        }),
        k.layer('background'),
        'mothers_room_background',
        'background',
      ])

      roomObj.add([
        k.sprite('mothersRoomHover', {
          width: getTiledSize(data.width),
          height: getTiledSize(data.height),
        }),
        k.layer('hover'),
        'mothers_room_hover',
        'hover',
      ])

      data.objects.forEach((obj: any) => {
        const poly = parsePolygon(k, obj)
        roomObj.add([k.pos(), k.area({ shape: poly }), k.body({ isStatic: true }), 'colliders'])
      })

      roomObj.add([
        k.level(
          [
            '          ',
            '          ',
            '          ',
            '          ',
            '         1',
            '    ?     ',
            '          ',
            '          ',
            '          ',
            '          ',
            '          ',
          ],
          {
            tileWidth: getTiledSize(),
            tileHeight: getTiledSize(),
            tiles: {
              '1': () => [
                k.rect(getTiledSize(0.5), getTiledSize(), { fill: false }),
                k.area({ isSensor: true }),
                'mothers_room_door_exit',
                'door_exit',
                'door',
              ],
              '?': () => [
                k.sprite('overlay', { anim: 'question' }),
                k.area({ isSensor: true }),
                k.layer('background'),
                'mothers_room_info',
                'sfx_emit_action',
                'info',
              ],
            },
          },
        ),
        k.layer('objects'),
        'mothers_room',
      ])

      // Set player position
      k.get('player')[0]?.setPos(k.vec2(getTiledSize(8.5), getTiledSize(4)))

      const doorExitCollision = k.onCollide('player', 'mothers_room_door_exit', () => {
        k.flash(k.BLACK, 0.5)
        roomObj.destroy()
        doorExitCollision.cancel()
        infoCollision.cancel()
        infoCollisionEnd.cancel()
        if (roomOpts.onExit) roomOpts.onExit()
      })

      const infoCollision = k.onCollide('player', 'mothers_room_info', () => {
        showTextFrame(getLangString('mothers_room_info'))
      })

      const infoCollisionEnd = k.onCollideEnd('player', 'mothers_room_info', () => {
        showTextFrame()
      })
    })

    return roomObj
  }

  return {
    setMothersRoom,
  }
}
